import * as Sentry from '@sentry/react';

type ErrorReportingContext = Record<string, unknown>;
type SanitizableSentryEvent = {
  message?: string;
  exception?: { values?: Array<{ value?: string }> };
  breadcrumbs?: Array<{ message?: string; data?: Record<string, unknown> }>;
  request?: {
    url?: string;
    cookies?: unknown;
    data?: unknown;
    headers?: unknown;
    query_string?: unknown;
  };
  user?: {
    id?: string | number;
    email?: unknown;
    ip_address?: unknown;
    username?: unknown;
  };
  extra?: Record<string, unknown>;
  contexts?: Record<string, unknown>;
};

const CLIENT_APP = 'tripalta-admin-ui';
const MAX_DEPTH = 4;
const MAX_ARRAY_LENGTH = 20;
const MAX_STRING_LENGTH = 1000;
const FILTERED = '[Filtered]';

const SENSITIVE_KEY_PATTERN =
  /authorization|auth|cookie|credential|email|first.?name|last.?name|full.?name|password|phone|refresh.?token|secret|session.?id|ssn|token|user.?id|username|jwt|card|payment/i;
const EMAIL_PATTERN = /[^\s@]+@[^\s@]+\.[^\s@]+/;
const TOKEN_LIKE_PATTERN = /bearer\s+[a-z0-9._-]+|eyJ[a-z0-9._-]+/i;

let initialized = false;

export function parseBoolean(
  value: string | boolean | undefined,
  defaultValue: boolean
): boolean {
  if (value === undefined) return defaultValue;
  if (typeof value === 'boolean') return value;
  return value.toLowerCase() === 'true';
}

function parseSampleRate(value: string | undefined): number {
  if (!value) return 0;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return Math.max(0, Math.min(1, parsed));
}

export function sanitizeUrl(url?: string): string | undefined {
  if (!url) return undefined;

  try {
    const base =
      typeof window !== 'undefined'
        ? window.location.origin
        : 'https://admin.tripalta.com';
    const parsed = new URL(url, base);
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    return undefined;
  }
}

function sanitizeString(value: string): string {
  if (EMAIL_PATTERN.test(value) || TOKEN_LIKE_PATTERN.test(value)) {
    return FILTERED;
  }

  const safeUrl = sanitizeUrl(value);
  if (safeUrl && (value.includes('?') || value.includes('#'))) {
    return safeUrl;
  }

  return value.length > MAX_STRING_LENGTH
    ? `${value.slice(0, MAX_STRING_LENGTH)}...`
    : value;
}

export function sanitizeForErrorReporting(
  value: unknown,
  depth = 0,
  seen = new WeakSet<object>()
): unknown {
  if (value === null || value === undefined) return value;

  if (typeof value === 'string') {
    return sanitizeString(value);
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }

  if (value instanceof Error) {
    return {
      name: value.name,
      message: sanitizeString(value.message),
    };
  }

  if (depth >= MAX_DEPTH) {
    return '[Truncated]';
  }

  if (Array.isArray(value)) {
    return value
      .slice(0, MAX_ARRAY_LENGTH)
      .map(item => sanitizeForErrorReporting(item, depth + 1, seen));
  }

  if (typeof value === 'object') {
    if (seen.has(value)) {
      return '[Circular]';
    }
    seen.add(value);

    const sanitized: Record<string, unknown> = {};
    Object.entries(value as Record<string, unknown>).forEach(
      ([key, itemValue]) => {
        if (SENSITIVE_KEY_PATTERN.test(key)) {
          sanitized[key] = FILTERED;
          return;
        }

        sanitized[key] = sanitizeForErrorReporting(itemValue, depth + 1, seen);
      }
    );
    return sanitized;
  }

  return String(value);
}

export function sanitizeSentryEvent<T extends SanitizableSentryEvent>(
  event: T
): T {
  try {
    if (typeof event.message === 'string') {
      event.message = sanitizeString(event.message);
    }

    for (const exception of event.exception?.values ?? []) {
      if (typeof exception.value === 'string') {
        exception.value = sanitizeString(exception.value);
      }
    }

    for (const breadcrumb of event.breadcrumbs ?? []) {
      if (typeof breadcrumb.message === 'string') {
        breadcrumb.message = sanitizeString(breadcrumb.message);
      }
      if (breadcrumb.data) {
        breadcrumb.data = sanitizeForErrorReporting(breadcrumb.data) as Record<
          string,
          unknown
        >;
      }
    }

    if (event.request) {
      event.request = {
        ...event.request,
        url: sanitizeUrl(event.request.url),
        cookies: undefined,
        data: undefined,
        headers: undefined,
        query_string: undefined,
      };
    }

    if (event.user) {
      event.user = {
        id: event.user.id ? FILTERED : undefined,
        email: undefined,
        ip_address: undefined,
        username: undefined,
      };
    }

    event.extra = sanitizeForErrorReporting(event.extra) as
      | Record<string, unknown>
      | undefined;
    event.contexts = sanitizeForErrorReporting(
      event.contexts
    ) as typeof event.contexts;
  } catch {
    // Never block event delivery on a sanitizer failure.
  }

  return event;
}

function getConfig() {
  const env = import.meta.env;
  const environment =
    env.VITE_ADMIN_SENTRY_ENVIRONMENT ||
    env.VITE_ENV ||
    env.MODE ||
    'development';
  const dsn = env.VITE_ADMIN_SENTRY_DSN?.trim() || '';
  const enabled =
    parseBoolean(env.VITE_ADMIN_SENTRY_ENABLED, env.PROD) &&
    Boolean(dsn) &&
    environment !== 'test';

  return {
    dsn,
    enabled,
    environment,
    release: env.VITE_ADMIN_SENTRY_RELEASE || env.VITE_APP_VERSION,
    tracesSampleRate: parseSampleRate(env.VITE_ADMIN_SENTRY_TRACES_SAMPLE_RATE),
  };
}

export function isErrorReportingEnabled(): boolean {
  return getConfig().enabled;
}

export function initErrorReporting(): boolean {
  const config = getConfig();

  if (!config.enabled || initialized) {
    return initialized;
  }

  try {
    Sentry.init({
      dsn: config.dsn,
      environment: config.environment,
      release: config.release,
      sendDefaultPii: false,
      tracesSampleRate: config.tracesSampleRate,
      // Browser noise that is never actionable for the admin UI.
      ignoreErrors: [
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications',
      ],
      // Errors thrown from browser-extension code, not our bundles.
      denyUrls: [
        /^chrome-extension:\/\//i,
        /^moz-extension:\/\//i,
        /^safari-(web-)?extension:\/\//i,
      ],
      beforeSend(event) {
        return sanitizeSentryEvent(event);
      },
    });

    Sentry.setTag('client_app', CLIENT_APP);
    initialized = true;
    return true;
  } catch {
    initialized = false;
    return false;
  }
}

function normalizeError(error: unknown): Error {
  if (error instanceof Error) return error;
  if (typeof error === 'string') return new Error(error);
  return new Error('Unknown admin client error');
}

export function captureError(
  error: unknown,
  context: ErrorReportingContext = {}
): string | undefined {
  if (!isErrorReportingEnabled()) {
    return undefined;
  }

  try {
    const normalizedError = normalizeError(error);
    const sanitizedContext = sanitizeForErrorReporting({
      ...context,
      page:
        typeof window !== 'undefined'
          ? sanitizeUrl(window.location.href)
          : undefined,
    }) as Record<string, unknown>;

    return Sentry.withScope(scope => {
      scope.setTag('client_app', CLIENT_APP);
      if (typeof sanitizedContext.source === 'string') {
        scope.setTag('source', sanitizedContext.source);
      }
      scope.setContext('tripalta_admin', sanitizedContext);
      return Sentry.captureException(normalizedError);
    });
  } catch {
    return undefined;
  }
}
